// WebSocket Transport - Connects to cloud server via WebSocket
// For use in browser/web environments

import type {
  CoworkSession,
  CoworkSessionSummary,
  CoworkConfig,
  CoworkConfigUpdate,
  CoworkPermissionRequest,
  CoworkPermissionResult,
  CoworkStartOptions,
  CoworkContinueOptions,
  CoworkUserMemoryEntry,
  CoworkMemoryStats,
  CoworkSandboxStatus,
  CoworkSandboxProgress,
  CoworkApiConfig,
} from '../types/cowork';

export type StreamEventType = 'message' | 'messageUpdate' | 'permissionRequest' | 'complete' | 'error' | 'scheduledTask:statusUpdate' | 'scheduledTask:runUpdate';

export interface StreamEventPayloads {
  message: { sessionId: string; message: CoworkSession['messages'][0] };
  messageUpdate: { sessionId: string; messageId: string; content: string };
  permissionRequest: { sessionId: string; request: CoworkPermissionRequest };
  complete: { sessionId: string; claudeSessionId: string | null };
  error: { sessionId: string; error: string };
  'scheduledTask:statusUpdate': unknown;
  'scheduledTask:runUpdate': unknown;
}

export interface TransportConfig {
  type: 'websocket';
  serverUrl: string;
  token?: string;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
}

export interface ApiResult<T = unknown> {
  id?: string;
  success: boolean;
  data?: T;
  error?: string;
}

export interface ConnectionStatus {
  connected: boolean;
  type: 'websocket';
  serverUrl?: string;
  error?: string;
  authenticated?: boolean;
  userId?: string;
}

type StreamListener<K extends StreamEventType> = (payload: StreamEventPayloads[K]) => void;

interface PendingRequest {
  resolve: (value: unknown) => void;
  reject: (error: Error) => void;
  timeout: ReturnType<typeof setTimeout>;
}

interface WebSocketMessage {
  id: string;
  method: string;
  params?: unknown;
  token?: string;
}

interface WebSocketResponse {
  id: string;
  success: boolean;
  data?: unknown;
  error?: string;
}

interface WebSocketEvent {
  type: 'event';
  event: string;
  payload: unknown;
  timestamp: number;
}

/**
 * WebSocket Transport - Connects to cloud server
 */
export class WebSocketTransport {
  private ws: WebSocket | null = null;
  private serverUrl: string;
  private token: string | null = null;
  private reconnectInterval: number;
  private maxReconnectAttempts: number;
  private reconnectAttempts = 0;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private manualClose = false;
  private pendingRequests: Map<string, PendingRequest> = new Map();
  private streamListeners: Map<string, Set<(...args: unknown[]) => void>> = new Map();
  private requestId = 0;
  private connectionStatus: ConnectionStatus = { connected: false, type: 'websocket' };
  private statusListeners: Set<(status: ConnectionStatus) => void> = new Set();

  constructor(config: TransportConfig) {
    this.serverUrl = config.serverUrl;
    this.token = config.token ?? null;
    this.reconnectInterval = config.reconnectInterval ?? 3000;
    this.maxReconnectAttempts = config.maxReconnectAttempts ?? 10;
  }

  // ============================================================================
  // Connection Management
  // ============================================================================

  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const url = new URL(this.serverUrl);
        if (this.token) {
          url.searchParams.set('token', this.token);
        }

        this.ws = new WebSocket(url.toString());

        this.ws.onopen = () => {
          console.log('[WebSocketTransport] Connected');
          this.manualClose = false;
          this.reconnectAttempts = 0;
          this.updateConnectionStatus({ 
            connected: true, 
            type: 'websocket', 
            serverUrl: this.serverUrl 
          });
          resolve();
        };

        this.ws.onclose = (event) => {
          console.log('[WebSocketTransport] Disconnected:', event.code, event.reason);
          for (const [, pending] of this.pendingRequests) {
            clearTimeout(pending.timeout);
            pending.reject(new Error('Connection lost'));
          }
          this.pendingRequests.clear();
          this.updateConnectionStatus({
            connected: false,
            type: 'websocket',
            serverUrl: this.serverUrl,
            error: `Disconnected: ${event.code} ${event.reason}`
          });
          this.handleReconnect();
        };

        this.ws.onerror = (error) => {
          console.error('[WebSocketTransport] Error:', error);
          this.updateConnectionStatus({ 
            connected: false, 
            type: 'websocket', 
            serverUrl: this.serverUrl,
            error: 'Connection error'
          });
          reject(new Error('WebSocket connection error'));
        };

        this.ws.onmessage = (event) => {
          this.handleMessage(event.data);
        };
      } catch (error) {
        reject(error);
      }
    });
  }

  disconnect(): void {
    this.manualClose = true;
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    for (const [, pending] of this.pendingRequests) {
      clearTimeout(pending.timeout);
      pending.reject(new Error('Disconnected'));
    }
    this.pendingRequests.clear();
  }

  private handleReconnect(): void {
    if (this.manualClose) {
      this.manualClose = false;
      return;
    }
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.log('[WebSocketTransport] Max reconnect attempts reached');
      return;
    }

    this.reconnectAttempts++;
    console.log(`[WebSocketTransport] Reconnecting in ${this.reconnectInterval}ms (attempt ${this.reconnectAttempts})`);
    
    this.reconnectTimeout = setTimeout(() => {
      this.connect().catch((error) => {
        console.error('[WebSocketTransport] Reconnect failed:', error);
      });
    }, this.reconnectInterval);
  }

  private updateConnectionStatus(status: Partial<ConnectionStatus>): void {
    this.connectionStatus = { ...this.connectionStatus, ...status };
    for (const listener of this.statusListeners) {
      listener(this.connectionStatus);
    }
  }

  getConnectionStatus(): ConnectionStatus {
    return this.connectionStatus;
  }

  onStatusChange(listener: (status: ConnectionStatus) => void): () => void {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  }

  setToken(token: string | null): void {
    this.token = token;
  }

  // ============================================================================
  // Message Handling
  // ============================================================================

  private handleMessage(data: string): void {
    try {
      const message = JSON.parse(data);

      // Handle event broadcast
      if (message.type === 'event') {
        this.handleEvent(message as WebSocketEvent);
        return;
      }

      // Handle response to request
      const response = message as WebSocketResponse;
      const pending = this.pendingRequests.get(response.id);
      if (pending) {
        clearTimeout(pending.timeout);
        this.pendingRequests.delete(response.id);
        console.log('[WebSocket] Received response:', response.id, 'success:', response.success, 'data:', response.data, 'error:', response.error);
        if (response.success) {
          // Check if the response data itself contains an error
          const data = response.data as any;
          if (data && data.success === false && data.error) {
            console.log('[WebSocket] Response data contains error:', response.id, data.error);
            pending.reject(new Error(data.error));
          } else {
            pending.resolve(response.data);
          }
        } else {
          // Extract error from various formats
          const errorMsg = response.error 
            || (response.data as any)?.error 
            || 'Unknown error';
          console.log('[WebSocket] Response error:', response.id, errorMsg, 'full:', JSON.stringify(response));
          pending.reject(new Error(errorMsg));
        }
      }
    } catch (error) {
      console.error('[WebSocketTransport] Failed to parse message:', error);
    }
  }

  private handleEvent(event: WebSocketEvent): void {
    const listeners = this.streamListeners.get(event.event);
    if (listeners) {
      for (const listener of listeners) {
        try {
          listener(event.payload);
        } catch (error) {
          console.error(`[WebSocketTransport] Error in listener for ${event.event}:`, error);
        }
      }
    }
  }

  private async send<T>(method: string, params?: unknown): Promise<T> {
    return new Promise((resolve, reject) => {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        reject(new Error('WebSocket not connected'));
        return;
      }

      const id = `${++this.requestId}`;
      const message: WebSocketMessage = { id, method, params };
      if (this.token) {
        message.token = this.token;
      }

      console.log('[WebSocket] Sending request:', id, method);

      const timeout = setTimeout(() => {
        this.pendingRequests.delete(id);
        reject(new Error('Request timeout'));
      }, 30000);

      this.pendingRequests.set(id, {
        resolve: resolve as (value: unknown) => void,
        reject,
        timeout,
      });

      this.ws.send(JSON.stringify(message));
    });
  }

  // ============================================================================
  // Cowork Session Methods
  // ============================================================================

  async startSession(options: CoworkStartOptions): Promise<ApiResult<{ session: CoworkSession }>> {
    return this.send('cowork:session:start', options);
  }

  async continueSession(options: CoworkContinueOptions): Promise<ApiResult<unknown>> {
    return this.send('cowork:session:continue', options);
  }

  async stopSession(sessionId: string): Promise<ApiResult<unknown>> {
    return this.send('cowork:session:stop', sessionId);
  }

  async getSession(sessionId: string): Promise<ApiResult<{ session: CoworkSession | null }>> {
    return this.send('cowork:session:get', sessionId);
  }

  async listSessions(): Promise<ApiResult<{ sessions: CoworkSessionSummary[] }>> {
    return this.send('cowork:session:list');
  }

  async deleteSession(sessionId: string): Promise<ApiResult<unknown>> {
    return this.send('cowork:session:delete', sessionId);
  }

  async deleteSessions(sessionIds: string[]): Promise<ApiResult<unknown>> {
    return this.send('cowork:session:deleteBatch', sessionIds);
  }

  async setSessionPinned(sessionId: string, pinned: boolean): Promise<ApiResult<unknown>> {
    return this.send('cowork:session:pin', { sessionId, pinned });
  }

  async renameSession(sessionId: string, title: string): Promise<ApiResult<unknown>> {
    return this.send('cowork:session:rename', { sessionId, title });
  }

  // ============================================================================
  // Cowork Config Methods
  // ============================================================================

  async getConfig(): Promise<ApiResult<{ config: CoworkConfig }>> {
    return this.send('cowork:config:get');
  }

  async setConfig(config: CoworkConfigUpdate): Promise<ApiResult<unknown>> {
    return this.send('cowork:config:set', config);
  }

  // ============================================================================
  // Cowork Permission Methods
  // ============================================================================

  async respondToPermission(requestId: string, result: CoworkPermissionResult): Promise<ApiResult<unknown>> {
    return this.send('cowork:permission:respond', { requestId, result });
  }

  // ============================================================================
  // Cowork Memory Methods
  // ============================================================================

  async listMemoryEntries(input: {
    query?: string;
    status?: 'created' | 'stale' | 'deleted' | 'all';
    includeDeleted?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<ApiResult<{ entries: CoworkUserMemoryEntry[] }>> {
    return this.send('cowork:memory:listEntries', input);
  }

  async createMemoryEntry(input: {
    text: string;
    confidence?: number;
    isExplicit?: boolean;
  }): Promise<ApiResult<{ entry: CoworkUserMemoryEntry }>> {
    return this.send('cowork:memory:createEntry', input);
  }

  async updateMemoryEntry(input: {
    id: string;
    text?: string;
    confidence?: number;
    status?: 'created' | 'stale' | 'deleted';
    isExplicit?: boolean;
  }): Promise<ApiResult<{ entry: CoworkUserMemoryEntry }>> {
    return this.send('cowork:memory:updateEntry', input);
  }

  async deleteMemoryEntry(input: { id: string }): Promise<ApiResult<unknown>> {
    return this.send('cowork:memory:deleteEntry', input);
  }

  async getMemoryStats(): Promise<ApiResult<{ stats: CoworkMemoryStats }>> {
    return this.send('cowork:memory:getStats');
  }

  // ============================================================================
  // Sandbox Methods (not available in cloud - return stubs)
  // ============================================================================

  async getSandboxStatus(): Promise<CoworkSandboxStatus | null> {
    // Not available in cloud mode
    return { supported: false, runtimeReady: false, imageReady: false, downloading: false };
  }

  async installSandbox(): Promise<{ success: boolean; status: CoworkSandboxStatus; error?: string } | null> {
    // Not available in cloud mode
    return { success: false, status: await this.getSandboxStatus() ?? { supported: false, runtimeReady: false, imageReady: false, downloading: false }, error: 'Sandbox not available in cloud mode' };
  }

  onSandboxDownloadProgress(_callback: (progress: CoworkSandboxProgress) => void): () => void {
    // Not available in cloud mode
    return () => {};
  }

  // ============================================================================
  // API Config Methods (may be handled differently in cloud)
  // ============================================================================

  async getApiConfig(): Promise<CoworkApiConfig | null> {
    // In cloud mode, API config might be managed server-side
    try {
      const result = await this.send<{ apiKey?: string; baseURL?: string; model?: string; apiType?: string }>('api:getConfig');
      if (result && result.apiKey && result.baseURL && result.model) {
        return {
          apiKey: result.apiKey,
          baseURL: result.baseURL,
          model: result.model,
          apiType: result.apiType as 'anthropic' | 'openai' | undefined,
        };
      }
      return null;
    } catch {
      return null;
    }
  }

  async checkApiConfig(options?: { probeModel?: boolean }): Promise<{ hasConfig: boolean; config: CoworkApiConfig | null; error?: string } | null> {
    // In cloud mode, API config is managed server-side
    // Return hasConfig: true so the UI doesn't show the config warning
    try {
      const result = await this.send<{ hasConfig: boolean; config: CoworkApiConfig | null; error?: string }>('api:checkConfig', options);
      return result ?? null;
    } catch (error) {
      // If the API method doesn't exist, return hasConfig: true to avoid blocking the UI
      console.log('[WebSocket] api:checkConfig not available, returning default');
      return { hasConfig: true, config: null };
    }
  }

  async saveApiConfig(config: CoworkApiConfig): Promise<{ success: boolean; error?: string } | null> {
    // In cloud mode, API config is managed server-side
    try {
      return this.send('api:saveConfig', config);
    } catch (error) {
      console.log('[WebSocket] api:saveConfig not available, returning success');
      return { success: true };
    }
  }

  // ============================================================================
  // Store Methods
  // ============================================================================

  async storeGet<T = unknown>(key: string): Promise<T | undefined> {
    try {
      const result = await this.send<T>('store:get', key);
      return result;
    } catch {
      return undefined;
    }
  }

  async storeSet<T = unknown>(key: string, value: T): Promise<void> {
    await this.send('store:set', { key, value });
  }

  async storeDelete(key: string): Promise<void> {
    await this.send('store:remove', key);
  }

  // ============================================================================
  // Utility Methods (not available in cloud)
  // ============================================================================

  async generateSessionTitle(_prompt: string | null): Promise<string | null> {
    // Not available in cloud mode - could be implemented server-side
    return null;
  }

  async getRecentCwds(_limit?: number): Promise<string[]> {
    // Not available in cloud mode - could be implemented server-side
    return [];
  }

  // ============================================================================
  // Log Methods (cloud mode)
  // ============================================================================

  async listLogs(query?: {
    userId?: string;
    category?: string;
    status?: string;
    subjectId?: string;
    startTimeFrom?: number;
    startTimeTo?: number;
    limit?: number;
    offset?: number;
  }): Promise<{ success: boolean; logs?: import('../types/log').Log[]; error?: string }> {
    try {
      return this.send('log:list', query);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to list logs' };
    }
  }

  async getLog(id: string): Promise<{ success: boolean; log?: import('../types/log').Log; error?: string }> {
    try {
      return this.send('log:get', id);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to get log' };
    }
  }

  async createLog(input: import('../types/log').CreateLogInput): Promise<{ success: boolean; log?: import('../types/log').Log; error?: string }> {
    try {
      return this.send('log:create', input);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to create log' };
    }
  }

  async updateLog(id: string, input: import('../types/log').UpdateLogInput): Promise<{ success: boolean; log?: import('../types/log').Log; error?: string }> {
    try {
      return this.send('log:update', { id, input });
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to update log' };
    }
  }

  async deleteLog(id: string): Promise<{ success: boolean; error?: string }> {
    try {
      return this.send('log:delete', id);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to delete log' };
    }
  }

  async getLogSubjects(logId: string): Promise<{ success: boolean; subjects?: import('../types/log').Subject[]; error?: string }> {
    try {
      return this.send('log:getSubjects', logId);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to get log subjects' };
    }
  }

  async addSubjectToLog(logId: string, subjectId: string): Promise<{ success: boolean; result?: boolean; error?: string }> {
    try {
      return this.send('log:addSubject', { logId, subjectId });
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to add subject to log' };
    }
  }

  async removeSubjectFromLog(logId: string, subjectId: string): Promise<{ success: boolean; result?: boolean; error?: string }> {
    try {
      return this.send('log:removeSubject', { logId, subjectId });
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to remove subject from log' };
    }
  }

  // ============================================================================
  // Subject Methods (cloud mode)
  // ============================================================================

  async listSubjects(userId?: string): Promise<{ success: boolean; subjects?: import('../types/log').Subject[]; error?: string }> {
    try {
      return this.send('subject:list', userId);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to list subjects' };
    }
  }

  async getSubject(id: string): Promise<{ success: boolean; subject?: import('../types/log').Subject; error?: string }> {
    try {
      return this.send('subject:get', id);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to get subject' };
    }
  }

  async createSubject(input: import('../types/log').CreateSubjectInput): Promise<{ success: boolean; subject?: import('../types/log').Subject; error?: string }> {
    try {
      return this.send('subject:create', input);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to create subject' };
    }
  }

  async updateSubject(id: string, input: import('../types/log').UpdateSubjectInput): Promise<{ success: boolean; subject?: import('../types/log').Subject; error?: string }> {
    try {
      return this.send('subject:update', { id, input });
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to update subject' };
    }
  }

  async deleteSubject(id: string): Promise<{ success: boolean; error?: string }> {
    try {
      return this.send('subject:delete', id);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to delete subject' };
    }
  }

  async getSubjectLogs(subjectId: string): Promise<{ success: boolean; logs?: import('../types/log').Log[]; error?: string }> {
    try {
      return this.send('subject:getLogs', subjectId);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to get subject logs' };
    }
  }

  // ============================================================================
  // Gmail Methods
  // ============================================================================

  async getGmailStatus(): Promise<{ success: boolean; connected?: boolean; email?: string; error?: string; authUrl?: string }> {
    try {
      return await this.send('gmail:status');
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to get Gmail status' };
    }
  }

  async getGmailAuthUrl(): Promise<{ success: boolean; url?: string; error?: string }> {
    try {
      return await this.send('gmail:authUrl');
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to get auth URL' };
    }
  }

  async disconnectGmail(): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      return await this.send('gmail:disconnect');
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to disconnect Gmail' };
    }
  }

  async testGmailConnection(): Promise<{ success: boolean; email?: string; error?: string }> {
    try {
      return await this.send('gmail:test');
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Gmail test failed' };
    }
  }

  async listGmailMessages(query?: string, maxResults?: number, pageToken?: string): Promise<{
    success: boolean;
    messages?: Array<{ id: string; threadId: string }>;
    resultSizeEstimate?: number;
    nextPageToken?: string;
    error?: string;
  }> {
    try {
      return await this.send('gmail:listMessages', { query, maxResults, pageToken });
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to list messages' };
    }
  }

  async getGmailMessage(messageId: string): Promise<{ success: boolean; message?: unknown; error?: string }> {
    try {
      return await this.send('gmail:getMessage', messageId);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to get message' };
    }
  }

  async sendGmailMessage(params: {
    to: string; subject: string; body: string; cc?: string; bcc?: string;
    replyToMessageId?: string; html?: boolean;
  }): Promise<{ success: boolean; id?: string; threadId?: string; error?: string }> {
    try {
      return await this.send('gmail:sendMessage', params);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to send message' };
    }
  }

  async listGmailLabels(): Promise<{ success: boolean; labels?: Array<{ id: string; name: string; type: string }>; error?: string }> {
    try {
      return await this.send('gmail:listLabels');
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to list labels' };
    }
  }

  async modifyGmailMessage(messageId: string, addLabels?: string[], removeLabels?: string[]): Promise<{ success: boolean; id?: string; labelIds?: string[]; error?: string }> {
    try {
      return await this.send('gmail:modifyMessage', { messageId, addLabels, removeLabels });
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to modify message' };
    }
  }

  async deleteGmailMessage(messageId: string): Promise<{ success: boolean; error?: string }> {
    try {
      return await this.send('gmail:deleteMessage', messageId);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to delete message' };
    }
  }

  async getGmailAttachment(messageId: string, attachmentId: string): Promise<{ success: boolean; data?: string; size?: number; error?: string }> {
    try {
      return await this.send('gmail:getAttachment', { messageId, attachmentId });
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to get attachment' };
    }
  }

  // ============================================================================
  // Camera Frame Streaming (for model inference)
  // ============================================================================

  /**
   * Send a camera frame to the server for model inference.
   * Fire-and-forget: the server acks receipt, then sends the prediction back
   * as a 'detection:result' WS event.
   *
   * @param image Base64-encoded JPEG/PNG (with or without data: prefix)
   * @param stream 'mobile' or 'dashboard'
   * @returns Promise that resolves when the frame is ack'd (not when inference completes)
   */
  async sendCameraFrame(image: string, stream: 'mobile' | 'dashboard' = 'mobile'): Promise<{ success: boolean; queued?: boolean; error?: string; dropped?: boolean }> {
    return new Promise((resolve) => {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        resolve({ success: false, error: 'WebSocket not connected' });
        return;
      }

      const id = `${++this.requestId}`;
      const message: WebSocketMessage = { id, method: 'camera:frame', params: { image, stream } };
      if (this.token) {
        message.token = this.token;
      }

      // Short timeout — frame ack should be fast (server just queues it)
      const timeout = setTimeout(() => {
        this.pendingRequests.delete(id);
        resolve({ success: false, error: 'Ack timeout' });
      }, 5000);

      this.pendingRequests.set(id, {
        resolve: (value: unknown) => {
          const v = value as { success: boolean; queued?: boolean; error?: string; dropped?: boolean };
          resolve(v);
        },
        reject: (err: Error) => resolve({ success: false, error: err.message }),
        timeout,
      });

      this.ws.send(JSON.stringify(message));
    });
  }

  // ============================================================================
  // Stream Event Subscriptions
  // ============================================================================

  on<K extends StreamEventType>(
    event: K,
    listener: StreamListener<K>
  ): () => void {
    if (!this.streamListeners.has(event)) {
      this.streamListeners.set(event, new Set());
    }
    this.streamListeners.get(event)!.add(listener as (...args: unknown[]) => void);

    return () => {
      this.streamListeners.get(event)?.delete(listener as (...args: unknown[]) => void);
    };
  }

  off<K extends StreamEventType>(event: K, listener: StreamListener<K>): void {
    const listeners = this.streamListeners.get(event);
    if (listeners) {
      listeners.delete(listener as (...args: unknown[]) => void);
      if (listeners.size === 0) {
        this.streamListeners.delete(event);
      }
    }
  }

  onEvent(event: string, listener: (payload: unknown) => void): () => void {
    if (!this.streamListeners.has(event)) {
      this.streamListeners.set(event, new Set());
    }
    this.streamListeners.get(event)!.add(listener as (...args: unknown[]) => void);

    return () => {
      this.streamListeners.get(event)?.delete(listener as (...args: unknown[]) => void);
    };
  }

  async getGmailWatcherFilter(): Promise<{ success: boolean; filter?: Record<string, unknown>; error?: string }> {
    try {
      return await this.send('gmail:getWatcherFilter');
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to get watcher filter' };
    }
  }

  async setGmailWatcherFilter(filter: Record<string, unknown>): Promise<{ success: boolean; filter?: Record<string, unknown>; error?: string }> {
    try {
      return await this.send('gmail:setWatcherFilter', filter);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to set watcher filter' };
    }
  }

  // Clean up all listeners
  destroy(): void {
    this.disconnect();
    this.streamListeners.clear();
    this.statusListeners.clear();
  }

  // Check if this transport is available (always true for WebSocket, just needs connection)
  static isAvailable(): boolean {
    return true;
  }
}

export default WebSocketTransport;